import { useState, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import {
  PIShell,
  Drill,
  PIInsight,
  PINoteImage,
  PracticeLadder,
} from "@/components/pi/PIShell";
import { PianoDiagram, KeyHighlight } from "@/components/PianoDiagram";
import { LessonMidi } from "@/components/MidiPlayer";
import { KeyPicker } from "@/components/KeyPicker";
import { KeyProvider, useKeyOrDefault } from "@/theory/key";
import { midiToPitchName, tonicMidiForPc } from "@/theory/melody";
import { kickAudio, playSequence } from "@/audio/piano";

/**
 * Piano Intermediate · Lesson 10 — Exploring Thirds on the Piano.
 *
 * Diatonic thirds (skip-one-play-one) ascending and descending the scale.
 * Each pair is either a major or minor third depending on where it sits.
 * Four directional permutations (UU / DD / UD / DU). Spread thirds vs
 * close thirds.
 *
 * Bespoke visual: the "Thirds Arcs" diagram. The scale is plotted on a
 * horizontal line; each diatonic third pair is drawn as a curved arc
 * connecting its two notes. Major thirds are amber, minor thirds are
 * cyan. The student SEES the major-minor pattern of any scale at a glance.
 */

interface Permutation {
  id: string;
  label: string;
  description: string;
}

const PERMUTATIONS: Permutation[] = [
  { id: "uu", label: "Up — Up",     description: "Both the scale and each pair go up: classic ascending thirds." },
  { id: "dd", label: "Down — Down", description: "Both descend together. The mirror of UU." },
  { id: "ud", label: "Up — Down",   description: "Scale ascends, but each pair plays high-then-low. Cascading sound." },
  { id: "du", label: "Down — Up",   description: "Scale descends, but each pair plays low-then-high. Stair-step sound." },
];

export default function PianoIntermediate10() {
  const { lesson, course, accent } = findLesson("piano-intermediate", "10");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-intermediate"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyProvider defaultTonic="A" availableQualities={["major", "harmonic-minor", "natural-minor"]}>
        <PIShell
          title="Exploring Thirds on the Piano"
          exerciseGoal="Skip one note, play one. Walk the scale in pairs — every pair is its own little phrase, and it barely sounds like an exercise."
          bpm={84}
          difficulty="Intermediate"
        >
          <Body />
        </PIShell>
      </KeyProvider>
    </LessonShell>
  );
}

function Body() {
  const k = useKeyOrDefault();
  const tonicMidi = tonicMidiForPc(k.tonicPc);
  const [permIdx, setPermIdx] = useState(0);
  const [spread, setSpread] = useState(false);
  const perm = PERMUTATIONS[permIdx];

  // Build the seven diatonic third pairs of the current scale.
  // Pair n: scale-degree n and scale-degree (n+2). Played as third interval.
  const scaleSemis = k.quality === "major"
    ? [0, 2, 4, 5, 7, 9, 11]
    : k.quality === "harmonic-minor"
      ? [0, 2, 3, 5, 7, 8, 11]
      : [0, 2, 3, 5, 7, 8, 10]; // natural minor

  type Pair = { lower: number; upper: number; type: "Major" | "Minor" };
  const pairs: Pair[] = [];
  for (let i = 0; i < 7; i++) {
    const lower = scaleSemis[i];
    const upper = scaleSemis[(i + 2) % 7] + (i + 2 >= 7 ? 12 : 0);
    const interval = upper - lower;
    const type: "Major" | "Minor" = interval === 4 ? "Major" : "Minor";
    pairs.push({ lower, upper, type });
  }

  const playRun = async () => {
    kickAudio();
    const sec = 60 / 84 / 2;
    const events: { time: number; notes: string[]; duration: string }[] = [];
    let t = 0;
    const order = perm.id.startsWith("d") ? [...pairs].reverse() : pairs;
    for (let i = 0; i < order.length; i++) {
      const pair = order[i];
      const aSemis = pair.lower;
      const bSemis = spread ? pair.upper - 12 : pair.upper;
      const first = perm.id[1] === "u" ? aSemis : bSemis;
      const second = perm.id[1] === "u" ? bSemis : aSemis;
      events.push({
        time: t,
        notes: [midiToPitchName(tonicMidi + first, k.useFlats)],
        duration: "8n",
      });
      t += sec;
      events.push({
        time: t,
        notes: [midiToPitchName(tonicMidi + second, k.useFlats)],
        duration: "8n",
      });
      t += sec;
    }
    await playSequence(events, 84);
  };

  // Master keyboard: light up the seven scale notes
  const masterHighlights: KeyHighlight[] = scaleSemis.map((s, i) => ({
    pc: ((k.tonicPc + s) % 12 + 12) % 12,
    tone: i === 0 ? "root" : "highlight",
    label: ["1", "2", "3", "4", "5", "6", "7"][i],
  }));

  return (
    <>
      <StudyDeck>
        <KeyPicker />
        <PianoDiagram
          highlights={masterHighlights}
          octaves={2}
          startOctave={3}
          playable
          ariaLabel="The seven scale degrees in the current key"
        />
        <p className="mt-2 text-[12.5px] text-cyan-300/70">
          Click any scale degree on the keyboard above to hear it. Below
          is the SAME scale plotted as third pairs — major-third pairs in
          amber, minor-third pairs in cyan.
        </p>
      </StudyDeck>

      <FadeIn>
        <Drill number="1" label="The big idea" title="Skip one, play one">
          <p>
            A <strong>third</strong> is two notes a third apart in the scale —
            i.e. you skip the note in between. The seven scale degrees
            therefore make seven third pairs (1↔3, 2↔4, 3↔5, 4↔6, 5↔7, 6↔1,
            7↔2). Some of those pairs are <strong>major</strong> thirds (4
            semitones), some are <strong>minor</strong> thirds (3 semitones)
            — and the alternation of major and minor is what gives every
            scale its emotional weather.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "How important the thirds are, how melodic they are and obviously
            how harmonic they are. So I'm going to give you a drill or an
            exercise to help you with your study of thirds."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="2" label="The map" title="Major thirds vs minor thirds in the current scale">
          <ThirdsArcs pairs={pairs} keyOrDefault={k} scaleSemis={scaleSemis} />
          <p className="mt-2 text-[14px] text-cyan-50/90">
            In <strong>{k.tonicName} {k.quality === "major" ? "major" : k.quality === "harmonic-minor" ? "harmonic minor" : "natural minor"}</strong>,
            the major thirds (amber arcs) sit on degrees{" "}
            {pairs
              .map((p, i) => (p.type === "Major" ? `${i + 1}↔${((i + 2) % 7) + 1}` : null))
              .filter(Boolean)
              .join(", ")}
            ; the rest are minor thirds (cyan).
          </p>
          <PIInsight>
            <p>
              The <em>fingerprint</em> of a scale is its sequence of major
              and minor thirds. Major scales follow{" "}
              <strong>M-m-m-M-M-m-m</strong>. Natural minor is that same cycle
              begun six degrees later — <strong>m-m-M-m-m-M-M</strong> — which is
              exactly what "relative minor" means. Harmonic minor then raises the
              7th, and two of the pairs change with it:{" "}
              <strong>m-m-M-m-M-M-m</strong>. (The famous augmented second sits
              between degrees 6 and 7, so it is a <em>second</em> — you won't find
              it among these thirds. But the raised note that creates it is what
              re-colours the arcs.) Switch the quality picker above and watch them
              change.
            </p>
          </PIInsight>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="3" label="The four motions" title="Direction is its own dimension">
          <p>
            Scale and pair are independent directions. So you have FOUR
            ways to traverse the same set of thirds — they sound radically
            different.
          </p>
          <div className="my-3 grid grid-cols-2 gap-2 md:grid-cols-4">
            {PERMUTATIONS.map((p, i) => {
              const active = i === permIdx;
              return (
                <motion.button
                  key={p.id}
                  layout
                  onClick={() => setPermIdx(i)}
                  className={`rounded-xl border px-3 py-2 text-left transition-colors ${
                    active
                      ? "border-amber-400/60 bg-amber-500/10"
                      : "border-cyan-400/20 bg-cyan-500/5 hover:bg-cyan-500/15"
                  }`}
                >
                  <div className={`text-[13px] font-bold ${active ? "text-amber-200" : "text-cyan-100"}`}>
                    {p.label}
                  </div>
                  <div className="mt-0.5 text-[11px] leading-snug text-cyan-300/80">
                    {p.description}
                  </div>
                </motion.button>
              );
            })}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              onClick={playRun}
              className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-zinc-900 hover:bg-amber-300"
            >
              ▶ Play "{perm.label}" in {k.tonicName}
            </button>
            <label className="flex select-none items-center gap-2 text-[13px] text-cyan-200">
              <input
                type="checkbox"
                checked={spread}
                onChange={(e) => setSpread(e.target.checked)}
                className="h-3.5 w-3.5 accent-amber-400"
              />
              Spread thirds (drop the upper note an octave)
            </label>
          </div>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="4" label="Spread vs close" title="The same pair, two registers apart">
          <p>
            Move the upper note of every third <em>down</em> by an octave,
            below the root, and the third becomes a <strong>spread
            third</strong> — the same two notes, but now a sixth apart (a
            third turned upside down). On Jason's chart: close is A → C♯,
            spread is C♯ → A. It looks weird under the hand, and it sounds
            almost like a chord.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "The C sharp used to be above the root, I just took the thing and
            put it lower and now you got yourself what I call as a spread
            third exercise."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="5" label="Left hand" title="Pair it up — harmonically or as a pulse">
          <p>
            Two ways to pair the right hand's melodic thirds with the left:
          </p>
          <ul className="my-3 space-y-1 text-[14px] text-cyan-50/95">
            <li>
              · <strong>Harmonic pairing</strong>: LH plays the SAME thirds
              as the RH, but as a chord (both notes simultaneously). Half
              the speed of the right.
            </li>
            <li>
              · <strong>Pulse pairing</strong>: LH plays just the root,
              keeping the pulse. Steady. Lets the right hand breathe.
            </li>
          </ul>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="6" label="Play along" title="The thirds run with the chosen direction">
          <LessonMidi
            courseSlug="piano-intermediate"
            order="10"
            label={`Thirds · ${k.tonicName} ${k.quality === "major" ? "major" : k.quality === "harmonic-minor" ? "harmonic minor" : "natural minor"}`}
            originalBpm={84}
          />
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="7" label="Practice ladder" title="Sixteen minutes, four rungs">
          <PracticeLadder
            intro={
              <>
                Walk through every direction once before adding the
                spread-third twist. The minor and harmonic-minor variants
                are where the ear-training really happens.
              </>
            }
            rungs={[
              { title: "Up–up + down–down (close)", minutes: 4, accent: "amber",   detail: "Walk the scale in close thirds, both directions. Let the major/minor alternation settle in your ear." },
              { title: "Mixed directions (close)",   minutes: 4, accent: "cyan",    detail: "Up–down and down–up motions. Listen for the cascading sound — it's an arpeggio in disguise." },
              { title: "Spread thirds",              minutes: 4, accent: "rose",    detail: "Drop the upper note below the root. The thirds become sixths — same pairs, a completely different flavour." },
              { title: "Switch quality",             minutes: 4, accent: "emerald", detail: "Flip the quality picker to harmonic minor. Notice how the augmented-2nd leap between scale degrees 6 and 7 reshapes the major/minor pattern." },
            ]}
          />
        </Drill>
      </FadeIn>
    </>
  );
}

/* ── Bespoke visual: Thirds Arcs ── */
function ThirdsArcs({
  pairs,
  keyOrDefault,
  scaleSemis,
}: {
  pairs: { lower: number; upper: number; type: "Major" | "Minor" }[];
  keyOrDefault: ReturnType<typeof useKeyOrDefault>;
  scaleSemis: number[];
}) {
  // Plot 8 scale degrees (1..7 + octave) along an x-axis, draw an arc for each pair.
  const W = 760;
  const H = 200;
  const padX = 40;
  const xFor = (idx: number) => padX + (idx / 7) * (W - 2 * padX);
  const baseY = H - 36;

  return (
    <div className="my-4 rounded-2xl border border-cyan-400/30 bg-zinc-900/40 p-3">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
        {/* baseline */}
        <line x1={padX - 10} y1={baseY} x2={W - padX + 10} y2={baseY} stroke="#475569" strokeWidth="2" />

        {/* scale-degree dots */}
        {[...scaleSemis, scaleSemis[0]].map((s, i) => {
          const pc = ((keyOrDefault.tonicPc + s) % 12 + 12) % 12;
          const name = keyOrDefault.nameForPc(pc);
          const x = xFor(i);
          return (
            <g key={`d${i}`}>
              <circle cx={x} cy={baseY} r="6" fill="#FCD34D" stroke="#A16207" strokeWidth="1.5" />
              <text x={x} y={baseY + 24} textAnchor="middle" fontSize="13" fontWeight="700" fontFamily="Inter,system-ui" fill="#FCD34D">
                {name}
              </text>
              <text x={x} y={baseY - 12} textAnchor="middle" fontSize="11" fontFamily="JetBrains Mono,monospace" fill="#94A3B8">
                {i === 7 ? "8" : i + 1}
              </text>
            </g>
          );
        })}

        {/* arcs — third pairs */}
        {pairs.map((p, i) => {
          const x1 = xFor(i);
          const x2 = xFor(i + 2 >= 8 ? 7 : i + 2);
          const cx = (x1 + x2) / 2;
          const arcHeight = 36 + Math.abs(i - 3) * 6;
          const cy = baseY - arcHeight;
          const path = `M ${x1} ${baseY - 5} Q ${cx} ${cy} ${x2} ${baseY - 5}`;
          const color = p.type === "Major" ? "#F59E0B" : "#22D3EE";
          return (
            <motion.g
              key={`a${i}`}
              initial={{ opacity: 0, pathLength: 0 }}
              whileInView={{ opacity: 1, pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.07, duration: 0.55 }}
            >
              <motion.path d={path} stroke={color} strokeWidth="2.5" fill="none" />
              <text
                x={cx}
                y={cy - 6}
                textAnchor="middle"
                fontSize="10"
                fontFamily="JetBrains Mono,monospace"
                fontWeight="700"
                fill={color}
              >
                {p.type === "Major" ? "M3" : "m3"}
              </text>
            </motion.g>
          );
        })}
      </svg>
      <div className="mt-1 flex items-center gap-4 text-[11px] uppercase tracking-wider text-cyan-300/70">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-0.5 w-6 bg-amber-500" />
          Major third (4 semitones)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-0.5 w-6 bg-cyan-400" />
          Minor third (3 semitones)
        </span>
      </div>
    </div>
  );
}

/* ── shared ── */
function StudyDeck({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="relative mb-4 rounded-2xl border border-cyan-400/30 bg-zinc-950/85 p-3 shadow-[0_8px_30px_rgba(0,0,0,0.5)]">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between text-left">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">✦ Study deck</div>
          <div className="text-[12px] text-cyan-300/70">Key picker · master keyboard</div>
        </div>
        <span className="text-[12px] font-mono text-cyan-300">{open ? "▴" : "▾"}</span>
      </button>
      <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
        <div className="mt-3 space-y-3">{children}</div>
      </motion.div>
    </div>
  );
}

function FadeIn({ children }: { children: ReactNode }) {
  const reduced = useReducedMotion();
  return (
    <motion.div initial={reduced ? false : { opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: "easeOut" }}>
      {children}
    </motion.div>
  );
}

function PIQuote({ children, attribution }: { children: ReactNode; attribution?: string }) {
  return (
    <figure className="my-4 rounded-2xl border-l-4 border-amber-400/80 bg-zinc-900/60 px-5 py-4">
      <blockquote className="text-[17px] leading-relaxed text-cyan-50/95" style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}>
        {children}
      </blockquote>
      {attribution && <figcaption className="mt-2 text-[11px] uppercase tracking-[0.18em] text-amber-300/80">— {attribution}</figcaption>}
    </figure>
  );
}
